import { i18n } from '@lingui/core'
import { DEFAULT_LOCALE, type SupportedLocale } from './locales'

/**
 * Load the shared catalog (newComponents, navigators, data) for a given locale.
 * English messages are compiled into the source via macros, so no catalog fetch
 * is needed for English.
 *
 * The .po import lives in a separate module (loadCatalog.ts) so it isn't
 * traversed by the Tamagui static extractor's esbuild config bundling.
 */
export const loadSharedCatalog = async (locale: SupportedLocale) => {
  if (locale === DEFAULT_LOCALE) return

  const { loadSharedCatalogMessages } = await import('./loadCatalog')
  const messages = await loadSharedCatalogMessages(locale)
  i18n.load(locale, messages)
  // Re-activate to notify Lingui subscribers so components re-render
  // with the real translations (activate was already called with an
  // empty catalog for instant UI switching)
  i18n.activate(locale)
}

/**
 * Activate a locale globally. Loads the shared catalog first if needed.
 * Note: loadSharedCatalog already calls i18n.activate() after loading,
 * so we only need to activate explicitly for the English early-return case.
 */
export const activateLocale = async (locale: SupportedLocale) => {
  if (locale === DEFAULT_LOCALE) {
    i18n.activate(locale)
    return
  }
  await loadSharedCatalog(locale)
}

// Activate the default locale synchronously on startup (no catalog needed for English)
i18n.load(DEFAULT_LOCALE, {})
i18n.activate(DEFAULT_LOCALE)

export { i18n }
